import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@mlm/ui';
import { listPosProducts } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';
import { InventoryLog } from '../components/inventory-log';

/**
 * Inventory tab (feature 13) — current stock of the branch. Reuses the same
 * branch-aware product catalog the checkout grid reads (`/pos/products`); this
 * is the read-only stock view with a name/SKU filter and a low-stock highlight.
 * Per-row "View log" opens the product's slice of the append-only
 * `inventory_movements` ledger in a side sheet (mirroring the admin Branch
 * page); the All movements card below is the same log branch-wide.
 */
export function InventoryPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [filter, setFilter] = useState('');
  const [logProduct, setLogProduct] = useState<{ id: string; name: string } | null>(null);

  const query = useQuery({
    queryKey: ['pos-inventory', branchId],
    enabled: !!branchId,
    queryFn: () => listPosProducts(branchId!),
  });

  const rows = (query.data ?? []).filter((p) => {
    const f = filter.trim().toLowerCase();
    if (!f) return true;
    return p.name.toLowerCase().includes(f) || p.sku.toLowerCase().includes(f);
  });

  const stockClass = (qty: number) =>
    qty <= 0 ? 'text-destructive' : qty < 5 ? 'text-warning' : '';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Branch inventory</CardTitle>
          <BranchPicker
            branches={branches}
            branchId={branchId}
            isScoped={isScoped}
            onChange={setSelectedBranchId}
          />
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name or SKU…"
            className="max-w-xs"
          />
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !rows.length ? (
            <p className="text-sm text-muted-foreground">No products match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-2 py-1.5">SKU</th>
                  <th className="px-2 py-1.5">Product</th>
                  <th className="px-2 py-1.5 text-right">Price</th>
                  <th className="px-2 py-1.5 text-right">On hand</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <Fragment key={p.id}>
                    <tr className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-2 py-1.5 font-mono text-xs">{p.sku}</td>
                      <td className="px-2 py-1.5">{p.name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        ₱{Number(p.effectivePrice).toFixed(2)}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums font-medium ${stockClass(p.qty)}`}
                      >
                        {p.qty}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="View movement log"
                          onClick={() => setLogProduct({ id: p.id, name: p.name })}
                        >
                          View log
                        </Button>
                      </td>
                    </tr>
                    {/* A variation product's stock lives per-variant — surface
                        the split the payload already carries. */}
                    {p.variants.map((v) => (
                      <tr key={v.id} className="border-b last:border-0 text-xs">
                        <td className="px-2 py-1 pl-6 font-mono text-muted-foreground">{v.sku}</td>
                        <td className="px-2 py-1 text-muted-foreground">↳ {v.name}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                          ₱{Number(v.price).toFixed(2)}
                        </td>
                        <td
                          className={`px-2 py-1 text-right tabular-nums ${stockClass(v.stockQty)}`}
                        >
                          {v.stockQty}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {branchId && (
        <Card>
          <CardHeader>
            <CardTitle>All movements</CardTitle>
          </CardHeader>
          <CardContent>
            <InventoryLog branchId={branchId} showFilters />
          </CardContent>
        </Card>
      )}

      <Sheet open={!!logProduct} onOpenChange={(o) => !o && setLogProduct(null)}>
        <SheetContent side="right" className="overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Inventory log{logProduct ? ` · ${logProduct.name}` : ''}</SheetTitle>
            <SheetDescription>
              Every stock movement for this product at this branch, newest first.
            </SheetDescription>
          </SheetHeader>
          {branchId && logProduct && (
            <div className="mt-4">
              <InventoryLog branchId={branchId} productId={logProduct.id} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
